import { supabase, recordSpeakerView, getSessionId } from './supabase';

// ── Speaker fetching ──────────────────────────────────────────

export const getSpeakers = async (options = {}) => {
  const { organization, search, featuredOnly, limit, offset } = options;

  let query = supabase
    .from('speakers')
    .select('*', { count: 'int' });

  if (featuredOnly) {
    query = query.eq('is_featured', true);
  }

  if (organization && organization !== 'all') {
    query = query.eq('organization', organization);
  }

  if (search) {
    const term = search.trim();
    query = query.or(`name.ilike.%${term}%,church_name.ilike.%${term}%,bio.ilike.%${term}%`);
  }

  query = query.order('name', { ascending: true });

  if (limit) query = query.limit(limit);
  if (offset) query = query.range(offset, offset + (limit || 50) - 1);

  const { data, error, count } = await query;
  return { data: data || [], error, count };
};

export const getFeaturedSpeakers = async () => {
  const { data, error } = await supabase
    .from('speakers')
    .select('*')
    .eq('is_featured', true)
    .order('name', { ascending: true });
  return { data: data || [], error };
};

export const getSpeakerById = async (id) => {
  const { data, error } = await supabase
    .from('speakers')
    .select('*')
    .eq('id', id)
    .single();
  return { data, error };
};

// ── Speaker events ────────────────────────────────────────────

export const getSpeakerEvents = async (speakerId) => {
  try {
    const { data, error } = await supabase
      .from('event_speakers')
      .select(`
        events (
          id, title, start_date, end_date, status,
          venue_name, location,
          organizations ( name ),
          venues ( name, city, state )
        )
      `)
      .eq('speaker_id', speakerId);

    if (error) throw error;
    return (data || []).map((e) => e.events).filter(Boolean);
  } catch (err) {
    console.error('Error fetching speaker events:', err);
    return [];
  }
};

export const getSpeakerUpcomingEvents = async (speakerId) => {
  const allEvents = await getSpeakerEvents(speakerId);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return allEvents
    .filter((e) => new Date(e.end_date || e.start_date) >= today)
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
};

export const getSpeakerPastEvents = async (speakerId) => {
  const allEvents = await getSpeakerEvents(speakerId);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return allEvents
    .filter((e) => new Date(e.end_date || e.start_date) < today)
    .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
};

// ── Favorites ─────────────────────────────────────────────────

export const getFavoriteSpeakerIds = async (userId) => {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from('user_favorite_speakers')
      .select('speaker_id')
      .eq('user_id', userId);
    if (error) return [];
    return (data || []).map((r) => r.speaker_id);
  } catch {
    return [];
  }
};

export const isFavoriteSpeaker = async (userId, speakerId) => {
  if (!userId || !speakerId) return false;
  try {
    const { data } = await supabase
      .from('user_favorite_speakers')
      .select('id')
      .eq('user_id', userId)
      .eq('speaker_id', speakerId)
      .single();
    return !!data;
  } catch {
    return false;
  }
};

export const toggleFavoriteSpeaker = async (userId, speakerId, currentlyFavorite) => {
  if (!userId) return { error: { message: 'Not authenticated' } };
  if (currentlyFavorite) {
    const { error } = await supabase
      .from('user_favorite_speakers')
      .delete()
      .eq('user_id', userId)
      .eq('speaker_id', speakerId);
    return { error, isFavorite: false };
  }
  const { error } = await supabase
    .from('user_favorite_speakers')
    .insert({ user_id: userId, speaker_id: speakerId });
  return { error, isFavorite: true };
};

// ── View tracking ─────────────────────────────────────────────

export { recordSpeakerView, getSessionId };

// ── Organizations ─────────────────────────────────────────────

export const getOrganizations = async () => {
  const { data, error } = await supabase
    .from('speakers')
    .select('organization')
    .not('organization', 'is', null)
    .order('organization', { ascending: true });
  if (error) return [];
  const unique = [...new Set((data || []).map((r) => r.organization).filter(Boolean))];
  return unique;
};

// ── Speaker profile creation ──────────────────────────────────

export const createSpeakerProfile = async (profileData) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: { message: 'Not authenticated' } };

  const payload = {
    ...profileData,
    user_id: user.id,
    is_verified: false,
    is_featured: false,
    profile_status: 'draft',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('speakers')
    .insert(payload)
    .select()
    .single();

  return { data, error };
};

export const updateSpeakerProfile = async (speakerId, updates) => {
  const { data, error } = await supabase
    .from('speakers')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', speakerId)
    .select()
    .single();
  return { data, error };
};

// ── Speaker invitations ───────────────────────────────────────

export const createSpeakerInvitation = async (invitationData) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: { message: 'Not authenticated' } };

  const payload = {
    ...invitationData,
    invited_by: user.id,
    status: 'sent',
    created_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('speaker_invitations')
    .insert(payload)
    .select()
    .single();

  return { data, error };
};

// ── Events owned by current user (for invitation modal) ───────

export const getUserEvents = async (userId) => {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from('events')
      .select('id, title, start_date, end_date, location, venue_name')
      .or(`created_by.eq.${userId},organization_id.in.(select id from organizations where owner_id.eq.${userId})`)
      .gte('start_date', new Date().toISOString().split('T')[0])
      .order('start_date', { ascending: true });
    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
};

// ── Image helper ──────────────────────────────────────────────

export const getSpeakerImage = (speaker) => {
  return speaker?.photo_url || speaker?.image_url || speaker?.image || null;
};

export const getSpeakerFallbackImage = (speaker) => {
  const name = speaker?.name || 'Speaker';
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6D28D9&color=fff&size=400`;
};
